import type { Logger } from "@infra-blocks/logger-interface";
import type { z } from "zod";
import { htmlDecode } from "../utils";

export type JsonBootstrapperParameters<S extends z.ZodType> = {
  schema: S;
  callback: (value: z.infer<S>) => void;
  logger?: Logger;
};

/**
Utility class that regroups the logic embedding a JSON script tag within a
custom element slot to bootstrap a property.

The bootstrapper is initialized in the custom element constructor with the
schema used to parsed and validate the JSON, and a callback that will dispatch
the parsed value when it was successful.

It must be linked in the rendering template as such:
html`...
<slot @slotchange={(e: Event) => this.jsonBootstrapper.slotChanged(e)}
...`;

In the many possible scenarios where the JSON cannot be parsed (missing element,
invalid element, invalid JSON, ...), then this implementation uses the provided
logger to emit warnings (when present) and skips calling the callback.
*/
export class JsonBootstrapper<S extends z.ZodType> {
  readonly #schema: S;
  readonly #callback: (value: z.infer<S>) => void;
  readonly #logger?: Logger;

  constructor(params: JsonBootstrapperParameters<S>) {
    const { schema, callback, logger } = params;
    this.#schema = schema;
    this.#callback = callback;
    this.#logger = logger;
  }

  /**
  Link this to the corresponding slot change event.
  */
  slotChanged(e: Event) {
    const slot = e.target as HTMLSlotElement | null;
    // If the slot is removed, then whatever state we have, we just keep.
    if (slot == null) {
      return;
    }
    const elements = slot.assignedElements();
    // Removing the subtree equates to the same as removing the slot.
    if (elements[0] == null) {
      return;
    }

    if (elements.length !== 1) {
      this.#logger?.warn("ignoring slot children beyond first child");
    }
    // The first child should be a script tag, and it's the only one we're interested in.
    this.#loadFrom(elements[0]);
  }

  #loadFrom(target: Element) {
    if (!(target instanceof HTMLScriptElement)) {
      this.#logger?.warn("invalid json slot element type", target);
      return;
    }

    const htmlDecoded = htmlDecode(target.textContent);
    let jsonTasks: unknown;
    try {
      jsonTasks = JSON.parse(htmlDecoded);
    } catch (err) {
      this.#logger?.warn("malformed json found in slot", err, target);
      return;
    }

    let parsed: z.infer<S>;
    try {
      parsed = this.#schema.parse(jsonTasks);
      this.#callback(parsed);
    } catch (err) {
      this.#logger?.warn("invalid json found in slot", err, target);
    }
  }
}
